import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Container,
    Card,
    AppBar,
    Toolbar,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    Alert
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useInvites } from "./contexts/ArtistContext";
import { acceptInvite } from "@firebasegen/default-connector";
import { useQueryClient } from "@tanstack/react-query";
import { dataConnect, auth, functions } from "./firebaseConfig";
import { httpsCallable } from "firebase/functions";
import Logger from "./utils/Logger";

const NoArtistsPage = () => {
    const { data: invites, refetch } = useInvites();
    const queryClient = useQueryClient();
    const [resolvingAlias, setResolvingAlias] = useState(false);
    const [aliasResolved, setAliasResolved] = useState(false);
    const [partyEvents, setPartyEvents] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const navigate = useNavigate();

    // Promoters and venues have no artist: their shows are under /partner
    useEffect(() => {
        httpsCallable(functions, 'listMyPartyEvents')()
            .then((res) => setPartyEvents(res.data.events || []))
            .catch((err) => Logger.error("NoArtistsPage", "Error loading partner shows", err));
        // Agencies, management and labels have a workspace under /org
        httpsCallable(functions, 'listMyOrganizations')()
            .then((res) => setOrgs(res.data.organizations || []))
            .catch((err) => Logger.error("NoArtistsPage", "Error loading organisations", err));
    }, []);

    useEffect(() => {
        Logger.info("NoArtistsPage", "NoArtistsPage rendered");
        
        const checkEmailAlias = async () => {
            const user = auth.currentUser;
            if (user && user.email && (!invites || invites.length === 0)) {
                const email = user.email.toLowerCase();
                if (email.endsWith("@gmail.com") || email.endsWith("@googlemail.com")) {
                    setResolvingAlias(true);
                    try {
                        const resolveEmailAliasConflict = httpsCallable(functions, 'resolveEmailAliasConflict');
                        const result = await resolveEmailAliasConflict();
                        if (result.data && result.data.success) {
                            setAliasResolved(true);
                            // Auto sign out after 4 seconds
                            setTimeout(() => {
                                auth.signOut().then(() => {
                                    window.location.reload();
                                });
                            }, 4000);
                        }
                    } catch (err) {
                        Logger.error("NoArtistsPage", "Error resolving email alias", err);
                    } finally {
                        setResolvingAlias(false);
                    }
                }
            }
        };

        checkEmailAlias();
    }, [invites]);

    const handleAccept = async (artistId) => {
        try {
            await acceptInvite(dataConnect, { artistId });
            // Full reload so the app re-evaluates routing (NoArtistsPage → main app)
            window.location.reload();
        } catch (error) {
            console.error("Error accepting invite:", error);
            alert("Failed to accept invite. Please try again.");
        }
    };

    if (aliasResolved) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                    We found a pending invite under a different email alias! 
                </Alert>
                <Typography variant="h6">
                    Please log in again to access your tour.
                </Typography>
                <CircularProgress sx={{ mt: 3 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: 'background.default' }}>
            {/* Page Header - Keeping basic branding */}
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: -0.5 }}>
                        Tourminator
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>

                {partyEvents.length > 0 && (
                    <Card sx={{ width: '100%', mb: 4, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" gutterBottom>
                            Your shows as promoter or venue
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            You work on {partyEvents.length} {partyEvents.length === 1 ? 'show' : 'shows'} with artists on Tourminator.
                        </Typography>
                        <Button variant="contained" onClick={() => navigate('/partner')}>
                            Open your shows
                        </Button>
                    </Card>
                )}

                {orgs.length > 0 && (
                    <Card sx={{ width: '100%', mb: 4, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" gutterBottom>
                            Your organisations
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            {orgs.map((o) => o.name).join(", ")}
                        </Typography>
                        <Button variant="contained" onClick={() => navigate('/org')}>
                            Open
                        </Button>
                    </Card>
                )}

                {/* Invitations Section */}
                {invites && invites.length > 0 && (
                    <Card sx={{ width: '100%', mb: 4, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" gutterBottom>
                            Pending Invitations
                        </Typography>
                        <List>
                            {invites.map((invite) => (
                                <ListItem key={invite.id} divider>
                                    <ListItemText
                                        primary={invite.artistName || "Unknown Artist"}
                                        secondary={`Role: ${invite.role || 'Member'}`}
                                    />
                                    <ListItemSecondaryAction>
                                        <Button size="small" variant="contained" color="primary" onClick={() => handleAccept(invite.artistId)}>
                                            Accept
                                        </Button>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Card>
                )}

                <Card sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Get an invitation from an artist or management to start using the app.
                        <br /><br />
                        If you are an artist or a manager and would like to use Tourminator, then get in touch.
                    </Typography>
                    <Button
                        variant="outlined"
                        href="mailto:contact@tourminator.com" // Placeholder email
                    >
                        Contact Support
                    </Button>
                    {orgs.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Button onClick={() => navigate('/org')}>
                                Booking agency, management or label? Create your workspace
                            </Button>
                        </Box>
                    )}
                    
                    {resolvingAlias && (
                        <Box sx={{ mt: 3 }}>
                            <CircularProgress size={24} sx={{ mr: 1, verticalAlign: 'middle' }} />
                            <Typography variant="caption" color="text.secondary">
                                Checking for alternative email aliases...
                            </Typography>
                        </Box>
                    )}
                </Card>
            </Container>
        </Box>
    );
};

export default NoArtistsPage;